import { useState, useMemo, useRef, useEffect } from "react";
import UtilService from "../../service/UtilService";
import AuditlogMetadataService from "../../service/AuditlogMetadataService";

const AuditlogEventTypeSelector = ({ selected, onChange }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const eventTypeGroups = AuditlogMetadataService.getEventTypeGroups();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return eventTypeGroups;

    const filtered = {};
    for (const [group, types] of Object.entries(eventTypeGroups)) {
      const matching = types.filter((t) =>
        t.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      if (matching.length > 0) {
        filtered[group] = matching;
      }
    }
    return filtered;
  }, [searchTerm, eventTypeGroups]);

  const removeType = (type) => {
    onChange(selected.filter((t) => t !== type));
  };

  const toggleType = (type) => {
    if (selected.includes(type)) {
      onChange(selected.filter((t) => t !== type));
    } else {
      onChange([...selected, type]);
    }
  };

  const selectAllInGroup = (types) => {
    const newSelected = [...selected];
    types.forEach((type) => {
      if (!newSelected.includes(type)) {
        newSelected.push(type);
      }
    });
    onChange(newSelected);
  };

  const deselectAllInGroup = (types) => {
    onChange(selected.filter((t) => !types.includes(t)));
  };

  return (
    <div className="auditlog-event-type-selector" ref={dropdownRef}>
      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="selected-chips">
          {selected.map((type) => (
            <span key={type} className="chip">
              {type}
              <span onClick={() => removeType(type)}>&times;</span>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <input
        type="text"
        placeholder="Search event types..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setShowDropdown(true)}
      />

      {/* Grouped dropdown */}
      {showDropdown && (
        <div className="event-type-dropdown">
          {Object.entries(filteredGroups).map(([group, types]) => {
            const allSelected = types.every((t) => selected.includes(t));
            const someSelected = types.some((t) => selected.includes(t));

            return (
              <div key={group} className="event-type-group">
                <div className="group-header">
                  <strong>{group}</strong>
                  <div>
                    {!allSelected && (
                      <button
                        type="button"
                        onClick={() => selectAllInGroup(types)}
                      >
                        Select All
                      </button>
                    )}
                    {someSelected && (
                      <button
                        type="button"
                        onClick={() => deselectAllInGroup(types)}
                      >
                        Deselect All
                      </button>
                    )}
                  </div>
                </div>
                {types.map((type) => (
                  <label key={type}>
                    <input
                      type="checkbox"
                      checked={selected.includes(type)}
                      onChange={() => toggleType(type)}
                    />
                    {type}
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AuditlogEventTypeSelector;
